import { Input } from '../Form/Input';
import { TextArea } from '../Form/TextArea';

import { Button } from '../Form/Button';
import ComboBox from '../Form/ComboBox';
import { useCallback, useMemo, useState } from 'react';
import { useDiagramStore } from '@diagram/ui/hooks/diagram-store.hook';
import { DrillDownModel } from '@diagram/model/drilldown.model';

type DrillDownFormProps = {
  setIsOpen?: (isOpen: boolean) => void;
};

export const DrillDownForm: React.FC<DrillDownFormProps> = ({ setIsOpen }) => {
  const patchDrilldown = useDiagramStore(
    (state) => state.drilldown.patchDrilldown
  );
  const activeDrilldownId = useDiagramStore((state) => state.activeDrilldown);
  const activeCategory = useDiagramStore((state) => state.activeCategory);
  const drillDowns = useDiagramStore((state) => state.drilldowns);
  const rfNodes = useDiagramStore((state) => state.rfNodes);
  const rfEdges = useDiagramStore((state) => state.rfEdges);

  const activeDrilldown = useMemo(() => {
    return activeDrilldownId
      ? drillDowns[activeDrilldownId]
      : {
          name: '',
          description: '',
          color: '',
          categoryId: activeCategory || '',
        };
  }, [activeDrilldownId, drillDowns, activeCategory]);

  const { nodes, edges } = useMemo(() => {
    const nodes = rfNodes.filter((n) => n.selected);
    const edges = rfEdges.filter((e) => e.selected);
    return { nodes, edges };
  }, [rfNodes, rfEdges]);

  const { name, description, color, categoryId } = activeDrilldown || {};
  const type = setIsOpen ? 'add' : 'update';

  const [formData, setFormData] = useState<Partial<DrillDownModel>>({
    name: name || '',
    description: description || '',
    color: color || '#000000',
    categoryId: categoryId || '',
  });

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      console.log('formData submitted:', formData);
      patchDrilldown({
        ...formData,
        nodes: nodes.map((n) => n.id),
        edges: edges.map((e) => e.id),
      });

      if (setIsOpen) {
        setIsOpen(false);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData, nodes, edges, setIsOpen]
  );

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name: fieldName, value } = e.target;
    setFormData({ ...formData, [fieldName]: value });
  };

  return (
    <form className="space-y-3 flex flex-col w-full" onSubmit={handleSubmit}>
      <div className="flex gap-x-2 w-full">
        <Input
          name="name"
          label="Name"
          type="text"
          onChange={handleChange}
          value={formData.name}
          width="w-full"
          variant="text"
        />
        <Input
          name="color"
          onChange={handleChange}
          value={formData.color}
          label="Color"
          type="color"
          className="h-9 w-9"
          width="w-1/4"
          variant="color"
        />
      </div>
      <ComboBox formdata={formData} setFormData={setFormData} />
      <TextArea
        name="description"
        label="Description"
        value={formData.description || ''}
        onChange={handleChange}
      />
      <Button type="submit" variant="primary" className="capitalize">
        {type} DrillDown
      </Button>
    </form>
  );
};
